import { noteCalendlyPopupOpened } from './booking-owner';
import type { Form } from '../../payload-types';

const CALENDLY_SCRIPT_SRC = 'https://assets.calendly.com/assets/external/widget.js';
const CALENDLY_CSS_HREF = 'https://assets.calendly.com/assets/external/widget.css';

type CalendlyGlobal = {
  initPopupWidget: (options: { url: string; rootElement?: HTMLElement }) => void;
};

declare global {
  interface Window {
    Calendly?: CalendlyGlobal;
  }
}

let scriptPromise: Promise<void> | null = null;

/**
 * Idempotently injects Calendly's widget script and stylesheet, resolving
 * once `window.Calendly` is ready. Safe to call from SSR — resolves to
 * a no-op when `window` is undefined.
 */
export function loadCalendly(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  if (window.Calendly) {
    return Promise.resolve();
  }
  if (scriptPromise) {
    return scriptPromise;
  }

  if (!document.querySelector(`link[href="${CALENDLY_CSS_HREF}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = CALENDLY_CSS_HREF;
    document.head.appendChild(link);
  }

  const promise = new Promise<void>((resolve, reject) => {
    const failWith = (script: HTMLScriptElement) => () => {
      // Remove the failed tag so a retry creates a fresh one instead of
      // attaching listeners to a dead script that will never fire `load` again.
      script.parentNode?.removeChild(script);
      reject(new Error('Calendly script failed to load'));
    };

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${CALENDLY_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', failWith(existing));
      return;
    }
    const script = document.createElement('script');
    script.src = CALENDLY_SCRIPT_SRC;
    script.async = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', failWith(script));
    document.head.appendChild(script);
  });

  // Clear the cache on failure so a later call can retry from a clean slate
  // instead of being permanently stuck with a rejected promise.
  promise.catch(() => {
    if (scriptPromise === promise) {
      scriptPromise = null;
    }
  });

  scriptPromise = promise;
  return promise;
}

/**
 * Opens Calendly's popup widget for the given event URL. Loads the
 * widget script + stylesheet on demand the first time it's called.
 *
 * Notes `formId` as the popup's Form as it opens, so a Booking made in it
 * credits that Form alone (`booking-owner.ts`).
 */
export async function openCalendlyPopup(url: string, formId: Form['id']): Promise<void> {
  await loadCalendly();
  if (typeof window === 'undefined' || !window.Calendly) {
    return;
  }
  noteCalendlyPopupOpened(formId);
  window.Calendly.initPopupWidget({ url, rootElement: document.body });
}

/**
 * Test-only reset of the cached script promise.
 */
export function _resetCalendlyForTests(): void {
  scriptPromise = null;
}
